/**
 * Aggregated Review List Component
 * 
 * Displays all proposals that need review in an aggregated list view.
 */

import React, { useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Input, Select, Button, useWorkspace, listReports, type Report } from '@tracekit/core';
import { ProposalReviewCard } from './ProposalReviewCard';
import { AggregatedReview, ReviewFilters } from '../types/review';
import { Filter, X } from 'lucide-react';

interface AggregatedReviewListProps {
  filters?: ReviewFilters;
  onProposalClick?: (reportId: string) => void;
  onStatusChange?: (reportId: string, newStatus: string) => void;
  canChangeStatus?: boolean;
  isReviewer?: boolean;
  onClientNamesExtracted?: (clientNames: string[]) => void;
  onReviewersExtracted?: (reviewers: string[]) => void;
}

export const AggregatedReviewList: React.FC<AggregatedReviewListProps> = ({
  filters = {},
  onProposalClick,
  onStatusChange,
  canChangeStatus = true,
  isReviewer = false,
  onClientNamesExtracted,
  onReviewersExtracted,
}) => {
  const { projectId } = useWorkspace();

  // Fetch all reports
  const { data: reports, isLoading, error } = useQuery({
    queryKey: ['aggregated-reviews', projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const reportsList = await listReports(projectId);
      return reportsList;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchInterval: 30000, // Refresh every 30s
  });

  // Extract unique client names and reviewers for filters
  useEffect(() => {
    if (reports) {
      const uniqueClientNames = new Set<string>();
      const uniqueReviewers = new Set<string>();
      reports.forEach((report: Report) => {
        const metadata = report.metadata || {};
        // Use name with fallback to client_name
        const displayName = report.name || report.client_name || metadata.client_name;
        if (displayName) uniqueClientNames.add(displayName);
        const reviewer = metadata.current_reviewer || metadata.reviewer;
        if (reviewer) uniqueReviewers.add(reviewer);
      });
      onClientNamesExtracted?.(Array.from(uniqueClientNames).sort());
      onReviewersExtracted?.(Array.from(uniqueReviewers).sort());
    }
  }, [reports, onClientNamesExtracted, onReviewersExtracted]);

  // Transform and filter reports to AggregatedReview format
  const aggregatedReviews: AggregatedReview[] = useMemo(() => {
    if (!reports) return [];

    const filtered = reports
      .filter((report: Report) => {
        // Filter for RFP proposals - handle both direct rfp_proposal type and custom with override
        const metadata = report.metadata || {};
        const isRfpProposal =
          report.report_type === 'rfp_proposal' ||
          (report.report_type === 'custom' &&
           (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id));

        if (!isRfpProposal) return false;

        // Map 'completed' to 'draft' for review purposes
        const reviewStatus = report.status === 'completed' ? 'draft' : report.status;
        
        // Apply status filter
        if (filters.status && filters.status !== 'all') {
          // If specific status is selected, only show that status
          if (reviewStatus !== filters.status) {
            return false;
          }
        } else {
          // Default (no filter or 'all'): only show draft/in_review (awaiting review)
          const isAwaitingReview = reviewStatus === 'draft' || reviewStatus === 'in_review';
          if (!isAwaitingReview) return false;
        }

        if (filters.clientName) {
          // Use name with fallback to client_name
          const displayName = report.name || report.client_name || metadata.client_name || '';
          if (displayName !== filters.clientName) {
            return false;
          }
        }

        if (filters.dateRange?.start || filters.dateRange?.end) {
          const reportDate = new Date(report.created_at || report.updated_at || '');
          if (filters.dateRange.start) {
            const startDate = new Date(filters.dateRange.start);
            if (reportDate < startDate) return false;
          }
          if (filters.dateRange.end) {
            const endDate = new Date(filters.dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            if (reportDate > endDate) return false;
          }
        }

        if (filters.reviewer) {
          const reviewer = metadata.current_reviewer || metadata.reviewer || '';
          if (reviewer !== filters.reviewer) {
            return false;
          }
        }

        return true;
      });

    return filtered
      .map((report: Report) => {
        const metadata = report.metadata || {};
        // Map status: 'completed' -> 'draft', default to 'draft' if unknown
        let reviewStatus: AggregatedReview['status'] = 'draft';
        if (report.status === 'completed') {
          reviewStatus = 'draft';
        } else if (['draft', 'in_review', 'approved', 'rejected', 'submitted', 'archived'].includes(report.status)) {
          reviewStatus = report.status as AggregatedReview['status'];
        }
        
        // Extract QA checklist completion from report
        const qaWriterChecklist = (report as any).qa_writer_checklist || {};
        const qaReviewerChecklist = (report as any).qa_reviewer_checklist || {};
        const qaPreSubmission = (report as any).qa_pre_submission_report || {};
        
        const writerCompletion = qaWriterChecklist.completion_percentage || 0;
        const reviewerCompletion = qaReviewerChecklist.completion_percentage || 0;
        const overallReady = qaPreSubmission.overall_ready || false;
        
        return {
          reportId: report.id,
          clientName: report.client_name || metadata.client_name || 'Unknown Client',
          proposalTitle: metadata.proposal_title || report.title || 'Untitled Proposal',
          name: report.name || metadata.name || metadata.proposal_title || report.title,
          projectName: metadata.project_name,
          status: reviewStatus,
          currentReviewer: metadata.current_reviewer || metadata.reviewer,
          commentsCount: metadata.comments_count || 0,
          lastUpdated: report.updated_at || report.created_at || '',
          qa_writer_checklist_completion: writerCompletion,
          qa_reviewer_checklist_completion: reviewerCompletion,
          qa_overall_ready: overallReady,
          deadline: metadata.deadline || metadata.submission_deadline || metadata.deadline_date,
          created_at: report.created_at,
          // Extract all proposal stats
          totalQuestions: report.total_questions,
          answeredQuestions: report.answered_questions,
          avgConfidence: report.avg_confidence,
          totalCitations: report.total_citations,
          evidenceGaps: report.evidence_gaps,
          viewCount: report.view_count,
          downloadCount: report.download_count,
          lastViewedAt: report.last_viewed_at,
          lastDownloadedAt: report.last_downloaded_at,
          metadata: metadata,
        };
      })
      .sort((a, b) => {
        // Sort by last updated (most recent first)
        return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
      });
  }, [reports, filters]);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="text-center py-12 text-gray-500">Loading proposals...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
        <p className="text-red-600 dark:text-red-400">Error loading proposals: {(error as Error).message}</p>
      </Card>
    );
  }

  if (aggregatedReviews.length === 0) {
    // Check if filters are active
    const hasActiveFilters = filters.status !== 'all' ||
      filters.clientName ||
      filters.dateRange ||
      filters.reviewer;

    return (
      <Card className="p-6">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {hasActiveFilters ? (
            <>
              <p className="text-lg font-medium mb-2">No proposals match your filters</p>
              <p className="text-sm">Try adjusting your filter criteria.</p>
            </>
          ) : (
            <>
              <p className="text-lg font-medium mb-2">No proposals awaiting review</p>
              <p className="text-sm">All proposals have been reviewed or are not in draft/in review status.</p>
            </>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {aggregatedReviews.map((review) => (
        <ProposalReviewCard
          key={review.reportId}
          review={review}
          onClick={onProposalClick}
          onStatusChange={onStatusChange}
          canChangeStatus={canChangeStatus}
          isReviewer={isReviewer}
        />
      ))}
    </div>
  );
};

